/**
 * pageCache - the last good read of each Office page, remembered across
 * unmounts (warmtabs).
 *
 * Founder, 3 Oct 2026, on /practice-crm: "when i click on any of these options
 * the screen turns like blank and then shows up ... it looks like it is loading
 * from a new thing everything with every click, doesnt look like data already
 * lives here. it happens in the whole office".
 *
 * `dayBookCache` fixed this for the Day Book on 10 Sep. Every other Office page
 * (CRM, Team, Client Requests, Partner Program) still kept its whole read in
 * component state, so each hop between tabs unmounted the page, threw the
 * answer away and drew a spinner over an empty screen while it asked again.
 * This is the same idea, one snapshot per page: the next mount paints what it
 * already knows at once and reads again underneath.
 *
 * The Day Book's three rules hold here too:
 *
 *  - **Keyed on WHO is asking and WHICH office.** A different login or another
 *    org is a miss and gets the spinner it deserves.
 *  - **Nothing older than `PAGE_STALE_MS` is served.** Past that the honest
 *    answer is to read again in front of the reader.
 *  - **Only what the server said.** No optimistic or derived state goes in.
 *
 * In memory only, never browser storage: a shared machine must not keep the
 * last practice's client list, and sign-out drops it (`useAuth.signOut`).
 */

/** Same window as the Day Book: a working session's hopping, not a lunch break. */
export const PAGE_STALE_MS = 12 * 60 * 1000;

/** The Office pages that remember themselves. One snapshot each. */
export type CachedPage = 'crm' | 'team' | 'requests' | 'partner';

const held = new Map<CachedPage, { key: string; at: number; data: unknown }>();

/** Null until we know whose page this is: an unkeyed snapshot is never read or written. */
export function pageKey(userId?: string | null, orgId?: string | null): string | null {
    return userId && orgId ? `${userId}|${orgId}` : null;
}

export interface PageSnapshot<T> {
    data: T;
    /** `Date.now()` when the server answered. Carried back in on every write. */
    at: number;
}

/** The remembered read for this page and key, or null if there is none or it is old. */
export function readPage<T>(page: CachedPage, key: string | null, now = Date.now()): PageSnapshot<T> | null {
    const h = held.get(page);
    if (!key || !h || h.key !== key) return null;
    if (now - h.at > PAGE_STALE_MS) { held.delete(page); return null; }
    return { data: h.data as T, at: h.at };
}

/**
 * Remember this read. One office per page: a new key replaces the old one.
 * `at` is when the server answered, not when this was called, so a page that
 * re-saves what it painted from here never makes an old read look new.
 */
export function writePage<T>(page: CachedPage, key: string | null, data: T, at = Date.now()): void {
    if (!key) return;
    held.set(page, { key, at, data });
}

/** Drop everything held. Sign-out, and the tests. */
export function forgetPages(): void {
    held.clear();
}
